import React, { memo, useCallback, useMemo, useState } from 'react';
import PinDots from 'src/react/Pin/Dots/PinDots';
import PinNumpad from 'src/react/Pin/Numpad/PinNumpad';
import Grid from '@mui/material/Grid';
import { ModalDialog } from 'src/react/_Layout/ModalDialog';
import { Box, DialogContent, useTheme, Typography } from '@mui/material';
import PinAvatar from 'src/react/Pin/Avatar/PinAvatar';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import { dialogContentActionButtonZIndex } from 'src/utils/zIndexConstants';
import LogoutDialog from 'src/react/_Layout/LogoutDialog';

type PinViewProps = {
  onCloseDialog?: () => void;
  dialogZIndex?: number;
  showGoBackButton?: boolean;
  showChangeUserButton?: boolean;
  avatarPicUrl: string | undefined;
  name: string;
  wrongPinEnteredCount: number;
  enteredPin: string;
  onPinDigitDeleted: (action?: 'all') => void;
  onPinDigitEntered: (digit: string) => void;
};

const PinView: React.FC<PinViewProps> = (props) => {
  const {
    onCloseDialog: onClose,
    dialogZIndex,
    showGoBackButton,
    showChangeUserButton,
    avatarPicUrl,
    name,
    wrongPinEnteredCount,
    enteredPin,
    onPinDigitDeleted,
    onPinDigitEntered,
  } = props;

  const { t } = useTranslation('pin');
  const theme = useTheme();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleCloseLogoutDialog = useCallback(() => {
    setShowLogoutDialog(false);
  }, []);

  const handleOpenLogoutDialog = useCallback(() => {
    setShowLogoutDialog(true);
  }, []);

  const mobileBottomPadding = useMemo(() => {
    return window.innerHeight < 340 ? 3 : 0;
  }, []);

  const pinErrorMessage = useMemo(() => {
    if (wrongPinEnteredCount !== 2) return null;
    return (
      <Typography sx={{ color: 'red', display: 'flex', justifyContent: 'center' }} variant="body2">
        {t('pin:lastRetry')}
      </Typography>
    );
  }, [wrongPinEnteredCount, t]);

  return (
    <ModalDialog zIndexSet={dialogZIndex} open appBarType="none" formDialog={false}>
      <DialogContent sx={{ overflowY: 'unset', p: 0 }}>
        {showGoBackButton && (
          <IconButton
            size="medium"
            onClick={handleClose}
            sx={{ position: 'absolute', zIndex: dialogContentActionButtonZIndex }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Box sx={{ backgroundColor: 'white', position: 'absolute', width: '100%', height: '100%' }}>
          <Grid container sx={{ height: '100%', overflowX: 'hidden' }} justifyContent="center" alignItems="center">
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center' }}>
                <img style={{ maxWidth: 210 }} src="images/Sekoia_Logo.svg" />
              </Box>
              <PinAvatar avatarPicUrl={avatarPicUrl} name={name} />
              {showChangeUserButton && (
                <Box sx={{ textAlign: 'center' }}>
                  <Button size="small" color="primary" onClick={handleOpenLogoutDialog}>
                    {t('pin:changeUser')}
                  </Button>
                </Box>
              )}
              {pinErrorMessage}
              <Box sx={{ pt: 2 }}>
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PinDots enteredPin={enteredPin} filledColor={theme.palette.grey[300]} />
                </Box>
              </Box>
              {!isMobile && (
                <Box sx={{ pt: 4, flex: 1, display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <PinNumpad onPinDigitEntered={onPinDigitEntered} onPinDigitDeleted={onPinDigitDeleted} />
                </Box>
              )}
              {showChangeUserButton && <LogoutDialog open={showLogoutDialog} onClose={handleCloseLogoutDialog} />}
            </Grid>
            {isMobile && (
              <Grid
                item
                xs={12}
                sx={{
                  pb: mobileBottomPadding,
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <PinNumpad onPinDigitEntered={onPinDigitEntered} onPinDigitDeleted={onPinDigitDeleted} />
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
    </ModalDialog>
  );
};

PinView.displayName = 'PinView';

export default memo(PinView);
